/**
 * Interprets the result codes Slack's two callbacks hand back to the SPA.
 *
 * The server answers both callbacks with a browser redirect rather than JSON, because
 * the browser arrives there by a top-level navigation from slack.com and there is no
 * caller to read a status code. The outcome therefore travels as a query parameter,
 * and this module is the only thing that reads it. Without an interpreter the codes
 * reach the app and nothing acts on them, which is how a failed link looks exactly
 * like a link that silently did nothing.
 *
 * Two separate parameter names, because the two callbacks land on different screens:
 *   - `?error=<code>` on `/slack/setup-complete`, from the workspace install callback.
 *   - `?slack=linked` or `?slack_error=<code>` from the per-user identity callback,
 *     landing on the organization's notifications settings, or on `/` for the two
 *     outcomes reached before membership is established.
 */

/** `?slack_error=<code>` - the per-user "Sign in with Slack" callback. */
export const SLACK_LINK_ERRORS: Record<string, string> = {
  // Landed at the SPA root: membership was never established or was disproven.
  user_mismatch:
    'That Slack link was started from a different Taskonio account. Sign in as that account and try again.',
  not_authorized: 'You are no longer a member of the organization that Slack link was started from.',

  // Landed on the organization's own settings: membership was re-verified first.
  no_installation: 'No Slack workspace is connected to this organization yet. An admin needs to connect one first.',
  team_mismatch:
    'You signed in to a different Slack workspace than the one connected here. Try again and pick the connected workspace.',
  slack_user_taken: 'That Slack account is already linked to another Taskonio user in this organization.',
  exchange_failed: 'Slack could not confirm that sign-in. Please try again.',
};

/** `?error=<code>` - the workspace install callback, which lands on the setup screen. */
export const SLACK_INSTALL_ERRORS: Record<string, string> = {
  missing_params: 'Slack sent us back without the details we need. Start the connection again from Integrations.',
  bad_state: 'This setup link has expired or was already used. Start the connection again from Settings, Integrations.',
  exchange_failed: 'Slack could not confirm the installation. Start the connection again from Settings, Integrations.',
  internal_error: 'Something went wrong while verifying the Slack workspace. Please try connecting again.',
};

/**
 * Falls back to a usable sentence rather than rendering a raw code at the user. An
 * unknown code means the server grew one this build does not know about, which is a
 * reason to say something generic, never a reason to say nothing.
 */
export function describeSlackLinkError(code: string): string {
  return SLACK_LINK_ERRORS[code] ?? 'Slack could not complete that link. Please try again.';
}

export function describeSlackInstallError(code: string): string {
  return SLACK_INSTALL_ERRORS[code] ?? 'Something went wrong while connecting Slack. Please try again.';
}
